import { Button, Icon, Modal, TextAreaInput } from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../locales/translations';
import { formatDate } from '../../../utils/dates';
import { StudentRiskEvaluationSessionEnrollment } from '../types';

type HistoryRecord = StudentRiskEvaluationSessionEnrollment['studyPlanEnrollment']['riskSessionEnrollments'][0];

export type StudentRiskEvaluationSessionsEnrollmentHistoryModalMethods = {
  open: (records: HistoryRecord[]) => void;
  close: () => void;
};

type StudentRiskEvaluationSessionsEnrollmentHistoryModalProps = {
  onClose?: () => void;
};

const StudentRiskEvaluationSessionsEnrollmentHistoryModal = (
  { onClose }: StudentRiskEvaluationSessionsEnrollmentHistoryModalProps,
  ref: Ref<StudentRiskEvaluationSessionsEnrollmentHistoryModalMethods>,
) => {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_RISK_EVALUATION_SESSIONS_LIST,
  );

  const [records, setRecords] = useState<HistoryRecord[]>([]);

  const recordsText = useMemo(() => {
    return records
      .map((record) =>
        t('historyModal.record', {
          definedRiskLevel: t(`riskLevels.${record.definedRiskLevel}`),
          definedAt: record.definedAt
            ? formatDate(record.definedAt, 'DD/MM/YYYY')
            : '',
        }),
      )
      .join('\n');
  }, [records, t]);

  const handleOpen = useCallback((nextRecords: HistoryRecord[]) => {
    const newNextRecords = [...nextRecords];
    newNextRecords.sort((a, b) => {
      const dateA = a.definedAt ? new Date(a.definedAt).getTime() : 0;
      const dateB = b.definedAt ? new Date(b.definedAt).getTime() : 0;
      return dateB - dateA;
    });
    setRecords(newNextRecords);
  }, []);

  const handleClose = useCallback(() => {
    setRecords([]);
    !!onClose && onClose();
  }, [onClose]);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  return (
    <Modal isOpen={!!records.length} toggle={handleClose}>
      <div className="d-flex flex-column align-items-center text-center">
        <Icon name="information" size={51} />
        <h2 className="text-dark fs-22 mt-4 mb-2 w-100 text-center">
          {t('historyModal.title')}
        </h2>
        <p className="fw-18 w-100 text-center">
          {t('historyModal.description')}
        </p>
        <div className="w-100 mb-5 mt-4">
          <TextAreaInput
            name="sessionrecords"
            label={t('historyModal.label')}
            value={recordsText}
            readOnly
            height={200}
            className="w-100 text-left"
          />
        </div>
        <div className="w-100">
          <Button
            className="w-100"
            type="button"
            size="md"
            fullwidth
            text={t('historyModal.confirm')}
            onClick={handleClose}
          />
        </div>
      </div>
    </Modal>
  );
};

export default forwardRef(StudentRiskEvaluationSessionsEnrollmentHistoryModal);
